import renderInputField from 'components/FormControls/textInput'
import { Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import emailValidator from 'utils/validators/emailValidator'
import urlValidator from 'utils/validators/urlValidator'
import * as Yup from 'yup'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/submitButtons'
import ProviderFields from 'modules/Forms/Account/Create/subcomponents/ProviderFields'
import omit from 'lodash/omit'
import ProviderNotification from 'modules/Forms/Account/Create/subcomponents/ProviderNotification'

export const GoogleAdsHighRiskSchema = () =>
  Yup.object().shape({
    network: Yup.string().required('common::validation_messages.required'),
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    email: Yup.string()
      .test('emailValidator', 'common::validation_messages.incorrect_email_format', emailValidator)
      .required('common::validation_messages.required'),
    product_url: Yup.string()
      .test('urlValidator', 'common::validation_messages.incorrect_url_format', urlValidator)
      .required('common::validation_messages.required'),
    provider_id: Yup.string().required('common::validation_messages.required'),
    creative_url: Yup.string().test(
      'urlValidator',
      'common::validation_messages.incorrect_url_format',
      urlValidator,
    ),
    cloaking_url: Yup.string().test(
      'urlValidator',
      'common::validation_messages.incorrect_url_format',
      urlValidator,
    ),
  })

let CreateGoogleAdsHighRiskAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  formHeader,
  networkId,
  form,
  initialValues,
  array,
  multiLogins,
}) => {
  const [currentProvider, setCurrentProvider] = useState(null)
  const { t } = useTranslation()
  const submitAccountData = useAccountCreateFormSubmit(form, onClose, multiLogins)

  const { email, user } = useSelector((state) => ({
    email: formValueSelector(form)(state, 'email'),
    user: state['app'].user,
  }))

  useEffect(() => {
    user.name && email === initialValues.email && autofill('email', user.name)
  }, [user])

  const submit = useCallback(
    (values) => submitAccountData(omit(values, ['geo', 'currency'])),
    [submitAccountData],
  )

  return (
    <>
      {formHeader}

      <Typography
        variant={'Body-20'}
        color="text-secondary"
        className={'margin-bottom-16'}
      >
        {t('services::forms.register_account.google-ads-high-risk.form_subtitle')}
      </Typography>

      <form
        onSubmit={handleSubmit(submit)}
        noValidate
      >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />

        <div className={'margin-bottom-16'}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.google-ads.fields.login.label'}
          />
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            name="email"
            type="email"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.google-ads.fields.email.label')}
            t={t}
          />
        </div>
        <div className={'margin-bottom-16'}>
          <Field
            name="product_url"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.google-ads.fields.product_url.label')}
            t={t}
          />
        </div>

        <ProviderFields
          autofill={autofill}
          form={form}
          networkId={networkId}
          onProviderChange={setCurrentProvider}
        />

        <div className={'margin-bottom-16'}>
          <Field
            name="creative_url"
            type="text"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.google-ads-high-risk.fields.creative_url.label',
            )}
            t={t}
          />
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            name="cloaking_url"
            type="text"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.google-ads-high-risk.fields.cloaking_url.label',
            )}
            t={t}
          />
        </div>

        {currentProvider && <ProviderNotification currentProvider={currentProvider} />}

        <SubmitButtons
          submitted={submitAccountData.submitted}
          submitting={submitting}
          onClose={onClose}
        />
      </form>
    </>
  )
}

CreateGoogleAdsHighRiskAccountForm = reduxForm({
  form: 'CreateGoogleAdsHighRiskAccountForm',
  initialValues: {
    network: '',
    login: '',
    product_url: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateGoogleAdsHighRiskAccountForm)

CreateGoogleAdsHighRiskAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  networkType: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  formHeader: PropTypes.node,
}

export default CreateGoogleAdsHighRiskAccountForm
